import { useEffect } from 'react';
import Head from 'next/head';
import type { GetStaticPaths, GetStaticProps } from 'next';

import { isReadinessStepFlow } from '@lib/page-utils/Readiness/progress';
import { isGytrStepFlow } from '@lib/page-utils/Gytr/progress';
import { sanityClient } from '@lib/sanity/client';
import {
  getAllQuizPageSlugsQuery,
  quizPageBySlugQuery
} from '@lib/sanity/queries';
import type { SanityQuizPage } from '@lib/sanity/types';
import QuizBlockRenderer from '@components/quiz/QuizBlockRenderer';
import { fireViewContent } from '@lib/analytics/fireViewContent';
import { toQuizPageDescription, toQuizPageTitle } from '@lib/seo';

// Time-based ISR: pages serve from the edge as static HTML and regenerate in
// the background at most this often, so traffic is fully decoupled from Sanity.
// A Sanity publish webhook can later swap this for on-demand revalidation.
const REVALIDATE_SECONDS = 60;

type QuizPageProps = {
  quizPage: SanityQuizPage;
  /** Read by _app to hide the site header/promo/footer on step-flow funnels */
  hideSiteChrome: boolean;
};

const QuizPage = ({ quizPage }: QuizPageProps) => {
  // FFH-606: View Content fires once when a funnel landing loads — the gytr /
  // mini-league-readiness step flows (the same set that hides site chrome).
  // fireViewContent is idempotent per session; the GTM pixel (off the
  // `view_content` dataLayer push) and the server CAPI dedup on one event_id.
  const isFunnelLanding =
    !!quizPage &&
    (isReadinessStepFlow(quizPage.blocks) || isGytrStepFlow(quizPage.blocks));

  useEffect(() => {
    if (isFunnelLanding) fireViewContent();
  }, [isFunnelLanding]);

  if (!quizPage) return null;

  return (
    <>
      <Head>
        <title>{toQuizPageTitle(quizPage.title)}</title>
        <meta
          name="description"
          content={toQuizPageDescription(quizPage.metaDescription)}
        />
        {/* This route renders any quizPage document by slug, which includes the
            funnels that already have a route of their own: /quiz/gytr-pre-gw1
            serves the same content as /ai-team-rating, the site's largest
            organic page. Nothing links here, the route is absent from the
            sitemap and Google has never crawled it, so it is a campaign and
            preview door rather than an organic entry point. Keeping it out of
            the index stops a future campaign link turning it into a duplicate
            Google has to choose between. Canonicalising instead would be wrong:
            the other funnel documents have no route to canonicalise to
            (FFH-2443). */}
        <meta name="robots" content="noindex" key="robots" />
      </Head>
      <QuizBlockRenderer quizPage={quizPage} />
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = await sanityClient
    .withConfig({ useCdn: false })
    .fetch<{ slug: string }[]>(getAllQuizPageSlugsQuery());

  return {
    paths: slugs
      .filter((entry) => entry.slug)
      .map((entry) => ({ params: { slug: entry.slug } })),
    // A quizPage published after the deploy builds on its first request
    // rather than 404ing, so new funnels need no deploy to go live.
    fallback: 'blocking'
  };
};

export const getStaticProps: GetStaticProps<QuizPageProps> = async ({
  params
}) => {
  const slug = params?.slug as string;
  // FFH-2596: this page was getServerSideProps, one billed Sanity query per
  // request (5.4k/day on the trial funnel alone). ISR caps Sanity load at
  // ~1 request/minute per slug regardless of traffic, and caches the render
  // rather than the response, so the middleware's set-cookie (geo-currency,
  // which quiz pages do want for pricing display) costs nothing here.
  // useCdn: false so the revalidation fetch bypasses Sanity's CDN: an
  // editor's change lands on the next revalidation (worst case ~60s) without
  // regional CDN inconsistency, and the residual ~1/min volume stays off the
  // billed CDN request quota.
  const quizPage = await sanityClient
    .withConfig({ useCdn: false })
    .fetch<SanityQuizPage | null>(quizPageBySlugQuery, { slug });

  if (!quizPage) {
    // Revalidate the 404 too, otherwise a slug requested before its document
    // is published stays 404 until the next deploy (the FFH-2559 trap).
    return { notFound: true, revalidate: REVALIDATE_SECONDS };
  }

  return {
    props: {
      quizPage,
      hideSiteChrome:
        isReadinessStepFlow(quizPage.blocks) || isGytrStepFlow(quizPage.blocks)
    },
    revalidate: REVALIDATE_SECONDS
  };
};

export default QuizPage;
